import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Vehicle } from '../types';
import { uploadImage } from '../api';
import EventHistory from './EventHistory';
import SpecsTab from './SpecsTab';
import OverviewTab from './OverviewTab';
import PublicListingTab from './PublicListingTab';
import EventModal from './EventModal';
import ImageCropModal from './ImageCropModal';
import PriceInput from './PriceInput';
import { useToast } from '../context/ToastContext';
import { useSettings } from '../context/SettingsContext';
import { kmToDisplay, displayToKm, distanceUnitLabel, litersToDisplay, displayToLiters, volumeUnitLabel } from '../utils/units';

type VehicleTab = 'overview' | 'stamdata' | 'history' | 'specs' | 'public';

interface Props {
  vehicle: Vehicle | null;
  onSave: (v: Vehicle) => void;
  // Faner der selv gemmer (fx Offentlig visning) melder det nye resultat her — uden at gemme igen.
  // Før kaldte de onSave, så bilen blev gemt to gange og der kom to "gemt"-beskeder.
  onSynced?: (v: Vehicle) => void;
  onClose: () => void;
  onDelete?: () => void;
  viewOnly?: boolean;
  // Bolge 3: 'page' renderer som almindelig side i stedet for modal-overlay.
  // Bruges af VehiclePage til /vehicles/:id/:tab-routerne.
  mode?: 'modal' | 'page';
  initialTab?: VehicleTab;
  onTabChange?: (tab: VehicleTab) => void;
  // Åbn én bestemt begivenhed i historik-fanen med det samme (fx fra en værksted-udgift)
  openEventType?: 'inspection' | 'maintenance' | 'checkup';
  openEventId?: number;
}

const empty: Vehicle = {
  registration_number: '', vin_number: '', make: '', model: '', variant: '',
  year: undefined, vehicle_type: undefined, fuel_type: undefined, color: '',
  tank_size: undefined, purchased_on: '', listed_on: '', purchase_price: undefined,
  purchase_vat_status: 'exempt', purchase_vat_amount: undefined,
  first_registered_on: '', sold_on: '', sales_price: undefined, summer_tires: '', winter_tires: '',
  status: 'active', image_url: '',
};

const toDateInput = (val: string | undefined | null): string => {
  if (!val) return '';
  return val.slice(0, 10);
};

const normalizeVehicleDates = (v: Vehicle): Vehicle => ({
  ...v,
  purchased_on: toDateInput(v.purchased_on),
  listed_on: toDateInput(v.listed_on),
  first_registered_on: toDateInput(v.first_registered_on),
  sold_on: toDateInput(v.sold_on),
});

export default function VehicleModal({ vehicle, onSave, onSynced, onClose, onDelete, viewOnly = false,
  mode = 'modal', initialTab, onTabChange, openEventType, openEventId }: Props) {
  const { t } = useTranslation();
  const { showError } = useToast();
  const { settings } = useSettings();
  const [tab, setTabState] = useState<VehicleTab>(initialTab ?? 'overview');
  const setTab = (next: VehicleTab) => { setTabState(next); onTabChange?.(next); };
  useEffect(() => { if (initialTab && initialTab !== tab) setTabState(initialTab); }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState<Vehicle>(vehicle ? normalizeVehicleDates(vehicle) : empty);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setForm(vehicle ? normalizeVehicleDates(vehicle) : empty);
  }, [vehicle]);

  const set = (field: keyof Vehicle, value: unknown) =>
    setForm(f => ({ ...f, [field]: value }));

  const distUnit = settings.distanceUnit;
  const distLabel = distanceUnitLabel(distUnit);
  const parseKmInput = (val: string) => {
    const num = parseInt(val.replace(/\./g, '').replace(/,/g, ''), 10);
    return isNaN(num) ? undefined : displayToKm(num, distUnit);
  };
  const parseNumInput = (val: string) => {
    const num = parseFloat(val.replace(/\./g, '').replace(',', '.'));
    return isNaN(num) ? undefined : num;
  };
  const fuelUnit = settings.fuelUnit;
  const fuelLabel = volumeUnitLabel(fuelUnit);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    // Reset input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleCropConfirm = async (blob: Blob, filename: string) => {
    setCropFile(null);
    setUploading(true);
    try {
      const file = new File([blob], filename, { type: 'image/jpeg' });
      // form.id mangler ved oprettelse — backend falder tilbage til rod-niveau, og
      // billedet flyttes til tenant-path naar vehicle har faaet et id (TODO).
      const url = await uploadImage(file, form.registration_number, form.id);
      set('image_url', url);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Upload fejlede');
    }
    setUploading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  const isPage = mode === 'page';

  // Wrapper-elementer skifter mellem modal og side. Indholdet (tabs + content) er identisk.
  const outerClass = isPage ? '' : 'modal-overlay';
  const panelClass = isPage
    ? 'relative bg-white rounded-xl shadow-card-sm max-w-4xl mx-auto my-6 flex flex-col overflow-hidden'
    : 'relative modal-panel max-w-2xl flex flex-col';

  return (
    <>
    {cropFile && (
      <ImageCropModal
        file={cropFile}
        onConfirm={handleCropConfirm}
        onCancel={() => { setCropFile(null); }}
      />
    )}
    <div className={outerClass}>
      <div className={panelClass} style={{ overflow: 'hidden' }}>
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="modal-title">
            {viewOnly ? vehicle?.registration_number
              : isPage ? (vehicle?.registration_number ?? t('modal.title_edit'))
              : vehicle?.id ? t('modal.title_edit')
              : t('modal.title_add')}
          </h2>
          {isPage ? (
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900">
              ← {t('modal.close')}
            </button>
          ) : (
            <button onClick={onClose} className="modal-close">&times;</button>
          )}
        </div>

        {/* Tabs — only shown when editing an existing vehicle */}
        {vehicle?.id && (
          <div className="flex border-b px-6 gap-4 flex-shrink-0">
            {(['overview', 'history', 'specs', 'public', 'stamdata'] as const).map(t_ => (
              <button key={t_} onClick={() => setTab(t_)}
                className={`py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t_ ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {t_ === 'overview' ? t('overview.tab')
                  : t_ === 'specs' ? t('specs.tab')
                  : t_ === 'public' ? 'Public listing'
                  : t(`events.${t_}`)}
              </button>
            ))}
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">

        {/* Overview tab */}
        {tab === 'overview' && vehicle?.id && (
          <OverviewTab vehicleId={vehicle.id} />
        )}

        {/* History tab */}
        {tab === 'history' && vehicle?.id && (
          <>
            <EventHistory key={historyKey} vehicle={vehicle} onRegister={() => setShowRegisterModal(true)} limit={20}
              openEventType={openEventType} openEventId={openEventId} />
            {!viewOnly && (
              <div className="px-6 pb-6 pt-2 border-t">
                <button
                  onClick={() => setShowRegisterModal(true)}
                  className="btn btn-accent">
                  + {t('events.register')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Specs tab */}
        {tab === 'specs' && vehicle?.id && (
          <SpecsTab vehicleId={vehicle.id} viewOnly={viewOnly} onClose={onClose} />
        )}

        {/* Public listing tab — Bolge 3 */}
        {tab === 'public' && vehicle?.id && (
          <PublicListingTab
            vehicle={form}
            viewOnly={viewOnly}
            onSaved={updated => { setForm(updated); onSynced?.(updated); }}
          />
        )}

        {/* Stamdata form */}
        <form onSubmit={handleSubmit} className={`p-6 space-y-4 ${tab !== 'stamdata' && vehicle?.id ? 'hidden' : ''}`}>
          {/* Registration + VIN */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('modal.field_reg')} *</label>
              <input required disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.registration_number}
                onChange={e => set('registration_number', e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className="label">{t('modal.field_vin')}</label>
              <input disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.vin_number ?? ''} onChange={e => set('vin_number', e.target.value)} />
            </div>
          </div>

          {/* Make + Model */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('modal.field_make')}</label>
              <input disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.make ?? ''} onChange={e => set('make', e.target.value)} />
            </div>
            <div>
              <label className="label">{t('modal.field_model')}</label>
              <input disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.model ?? ''} onChange={e => set('model', e.target.value)} />
            </div>
          </div>

          {/* Variant */}
          <div>
            <label className="label">{t('modal.field_variant')}</label>
            <input disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.variant ?? ''} onChange={e => set('variant', e.target.value)}
              placeholder={t('modal.field_variant_placeholder')} />
          </div>

          {/* Image */}
          <div>
            <label className="label">{t('modal.field_image')}</label>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            {uploading && <p className="text-sm text-[var(--color-primary)]">{t('modal.uploading')}</p>}
            {form.image_url ? (
              <div className="inline-block">
                <img src={form.image_url} alt="Preview" className="h-24 w-32 object-cover rounded-lg border" />
                {!viewOnly && (
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="block mt-1 text-xs italic text-gray-400 hover:text-gray-600">
                    {t('modal.change_image')}
                  </button>
                )}
              </div>
            ) : !viewOnly && (
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-cream-100">
                {t('modal.add_image')}
              </button>
            )}
          </div>

          {/* Year + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('modal.field_year')}</label>
              <input type="number" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.year ?? ''}
                onChange={e => set('year', e.target.value ? +e.target.value : undefined)} />
            </div>
            <div>
              <label className="label">{t('modal.field_type')}</label>
              <select disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.vehicle_type ?? ''} onChange={e => set('vehicle_type', e.target.value || undefined)}>
                <option value="">{t('modal.field_type_placeholder')}</option>
                {(['mini', 'bybil', 'stationcar', 'suv', 'varevogn', 'lastbil'] as const).map(t_ => (
                  <option key={t_} value={t_}>{t(`type.${t_}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fuel + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('modal.field_fuel')}</label>
              <select disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.fuel_type ?? ''} onChange={e => set('fuel_type', e.target.value || undefined)}>
                <option value="">{t('modal.field_fuel_placeholder')}</option>
                {(['benzin', 'diesel', 'el', 'hybrid'] as const).map(f => (
                  <option key={f} value={f}>{t(`fuel.${f}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">{t('modal.field_status')}</label>
              <select disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.status ?? 'active'} onChange={e => set('status', e.target.value)}>
                {(['active', 'service', 'sold'] as const).map(s => (
                  <option key={s} value={s}>{t(`status.${s}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Color + Tank */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('modal.field_color')}</label>
              <input disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.color ?? ''} onChange={e => set('color', e.target.value)} />
            </div>
            <div>
              <label className="label">{t('modal.field_tank')} ({fuelLabel})</label>
              <input type="number" step="0.01" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600"
                value={form.tank_size != null ? (litersToDisplay(form.tank_size, fuelUnit) ?? '') : ''}
                onChange={e => set('tank_size', e.target.value ? displayToLiters(+e.target.value, fuelUnit) : undefined)} />
            </div>
          </div>

          {/* Purchase price with VAT */}
          <PriceInput
            label={t('modal.field_price')}
            amount={form.purchase_price != null ? String(form.purchase_price) : ''}
            vatStatus={form.purchase_vat_status ?? 'exempt'}
            vatAmount={form.purchase_vat_amount != null ? String(form.purchase_vat_amount) : ''}
            onAmountChange={v => set('purchase_price', v ? parseFloat(v) : undefined)}
            onVatStatusChange={v => set('purchase_vat_status', v)}
            onVatAmountChange={v => set('purchase_vat_amount', v ? parseFloat(v) : undefined)}
            vatRate={settings.vatRate}
            disabled={viewOnly}
          />

          {/* Dates + purchase km */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="label">{t('modal.field_purchased_on')}</label>
              <input type="date" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.purchased_on ?? ''}
                onChange={e => set('purchased_on', e.target.value)} />
            </div>
            <div>
              <label className="label">{t('modal.field_listed_on')}</label>
              <input type="date" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.listed_on ?? ''}
                onChange={e => set('listed_on', e.target.value)} />
            </div>
            <div>
              <label className="label">{t('modal.field_first_registered_on')}</label>
              <input type="date" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.first_registered_on ?? ''}
                onChange={e => set('first_registered_on', e.target.value)} />
            </div>
            <div>
              <label className="label">{t('modal.field_purchase_km')} ({distLabel})</label>
              <input type="number" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600"
                value={form.purchase_km != null ? (kmToDisplay(form.purchase_km, distUnit) ?? '') : ''}
                onChange={e => set('purchase_km', e.target.value ? parseKmInput(e.target.value) : undefined)} />
            </div>
          </div>

          {/* Sold date + sales price — only shown when status is sold */}
          {form.status === 'sold' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">{t('modal.field_sold_on')}</label>
                <input type="date" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.sold_on ?? ''}
                  onChange={e => set('sold_on', e.target.value)} />
              </div>
              <div>
                <label className="label">{t('modal.field_sales_price')}</label>
                <input type="number" step="0.01" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600" value={form.sales_price ?? ''}
                  onChange={e => set('sales_price', e.target.value ? parseNumInput(e.target.value) : undefined)} />
              </div>
            </div>
          )}

          {/* Current km */}
          <div>
            <label className="label">
              {t('modal.field_current_km')} ({distLabel})
              {form.current_km_date && (
                <span className="font-normal text-gray-400 ml-1">— {t('modal.field_current_km_date', { date: form.current_km_date.slice(0, 10) })}</span>
              )}
            </label>
            <input type="number" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600"
              value={form.current_km != null ? (kmToDisplay(form.current_km, distUnit) ?? '') : ''}
              onChange={e => set('current_km', e.target.value ? parseKmInput(e.target.value) : undefined)} />
          </div>

          {/* Rental platforms */}
          <div className="border-t pt-4">
            <p className="label mb-2">{t('modal.rental_platforms')}</p>
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" disabled={viewOnly} className="rounded"
                  checked={!!form.rents_via_gomore}
                  onChange={e => set('rents_via_gomore', e.target.checked)} />
                <span className="text-sm text-gray-700">{t('modal.rents_via_gomore')}</span>
              </label>
              {form.rents_via_gomore && (
                <div className="ml-6">
                  <label className="label">{t('modal.gomore_car_id')}</label>
                  <input type="number" disabled={viewOnly} className="input disabled:bg-gray-50 disabled:text-gray-600 w-48"
                    placeholder={t('modal.gomore_car_id_placeholder')}
                    value={form.gomore_car_id ?? ''}
                    onChange={e => set('gomore_car_id', e.target.value ? +e.target.value : null)} />
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" disabled={viewOnly} className="rounded"
                  checked={!!form.rents_via_own}
                  onChange={e => set('rents_via_own', e.target.checked)} />
                <span className="text-sm text-gray-700">{t('modal.rents_via_own')}</span>
              </label>
            </div>
          </div>

          {!viewOnly && (
            <div className="flex items-center justify-between pt-2">
              {vehicle?.id && onDelete ? (
                <button type="button" onClick={() => setDeleteStep(1)}
                  className="btn btn-danger">
                  {t('modal.delete')}
                </button>
              ) : <span />}
              <div className="flex gap-3">
                {vehicle?.id ? (
                  <>
                    <button type="button" onClick={() => setForm(normalizeVehicleDates(vehicle))}
                      className="btn btn-secondary">
                      {t('modal.cancel')}
                    </button>
                    <button type="submit"
                      className="btn btn-primary">
                      {t('modal.save')}
                    </button>
                    <button type="button" onClick={onClose}
                      className="btn btn-secondary">
                      {t('modal.close')}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={onClose}
                      className="btn btn-secondary">
                      {t('modal.cancel')}
                    </button>
                    <button type="submit"
                      className="btn btn-primary">
                      {t('modal.submit')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </form>

        </div>{/* end scrollable content area */}

        {/* Delete confirmation overlay */}
        {deleteStep > 0 && (
          <div className="absolute inset-0 bg-white/97 rounded-xl flex flex-col items-center justify-center p-10 z-20">
            {deleteStep === 1 && (
              <>
                <p className="text-lg font-semibold text-gray-800 mb-2">{t('modal.delete_confirm1_title')}</p>
                <p className="text-sm text-gray-500 mb-8">{vehicle?.registration_number}</p>
                <div className="flex gap-3">
                  <button onClick={() => setDeleteStep(0)}
                    className="btn btn-secondary">
                    {t('modal.cancel')}
                  </button>
                  <button onClick={() => setDeleteStep(2)}
                    className="btn btn-danger">
                    {t('modal.delete_confirm1_continue')}
                  </button>
                </div>
              </>
            )}
            {deleteStep === 2 && (
              <>
                <p className="text-lg font-semibold text-[var(--color-error)] mb-2">{t('modal.delete_confirm2_title')}</p>
                <p className="text-sm text-gray-600 text-center max-w-sm mb-8">{t('modal.delete_confirm2_body')}</p>
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  {form.status !== 'sold' && (
                    <button onClick={() => { onSave({ ...form, status: 'sold' }); setDeleteStep(0); }}
                      className="btn btn-primary text-center">
                      {t('modal.delete_confirm2_sell')}
                    </button>
                  )}
                  <button onClick={onDelete}
                    className="btn btn-danger text-center">
                    {t('modal.delete_confirm2_delete')}
                  </button>
                  <button onClick={() => setDeleteStep(0)}
                    className="text-sm text-gray-400 hover:text-gray-600 text-center mt-1">
                    {t('modal.cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>

    {showRegisterModal && form.id && (
      <EventModal
        vehicle={form}
        onSaved={() => { setShowRegisterModal(false); setHistoryKey(k => k + 1); }}
        onClose={() => setShowRegisterModal(false)}
      />
    )}
    </>
  );
}
